// Version: 1.2
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from './auth';

function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

const ACTION_LABELS = {
  login:            { label: 'Login',             color: 'tag-green'  },
  login_blocked:    { label: 'Login blocked',      color: 'tag-amber'  },
  assessment_start: { label: 'Assessment started', color: 'tag-blue'   },
  user_create:      { label: 'User created',       color: 'tag-muted'  },
  user_block:       { label: 'User blocked',       color: 'tag-red'    },
  user_unblock:     { label: 'User unblocked',     color: 'tag-green'  },
  register:         { label: 'Registration',       color: 'tag-amber'  },
  client_rename:    { label: 'Client renamed',     color: 'tag-blue'   },
  user_edit:        { label: 'User edited',         color: 'tag-muted'  },
  user_delete:      { label: 'User deleted',         color: 'tag-red'    },
  password_change:  { label: 'Password changed',    color: 'tag-muted'  },
  user_mfa_toggle:  { label: 'MFA toggled',          color: 'tag-blue'   },
};

function formatDate(iso) {
  return new Date(iso + 'Z').toLocaleString('en-GB', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit', second:'2-digit'
  });
}

export default function AuditLog() {
  const isMobile = useIsMobile();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('');
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [pages,   setPages]   = useState(1);
  const LIMIT = 50;

  const fetchLog = useCallback((action = '', p = 1) => {
    setLoading(true);
    authFetch(`/api/audit?limit=${LIMIT}&page=${p}${action ? '&action=' + action : ''}`)
      .then(r => r.json())
      .then(d => {
        setEntries(Array.isArray(d) ? d : (d.entries || []));
        setTotal(d.total || 0);
        setPages(d.pages || 1);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchLog(filter, page); }, [filter, page]);

  const handleFilter = (action) => {
    setFilter(action);
    setPage(1);
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Audit Log</div>
        <div className="page-subtitle">Portal activity and access history · {total} entries</div>
      </div>

      <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap',alignItems:'center'}}>
        <button className={`btn btn-secondary btn-sm ${filter==='' ? ' active-filter' : ''}`} onClick={() => handleFilter('')}>All</button>
        {Object.entries(ACTION_LABELS).map(([key, val]) => (
          <button className={`btn btn-secondary btn-sm ${filter===key ? ' active-filter' : ''}`} key={key}
            onClick={() => handleFilter(key)}>
            {val.label}
          </button>
        ))}
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}
          onClick={() => fetchLog(filter, page)}>↻ Refresh</button>
      </div>

      {loading ? (
        <div style={{padding:48,textAlign:'center'}}><span className="spinner" /></div>
      ) : entries.length === 0 ? (
        <div className="card" style={{padding:48,textAlign:'center',color:'var(--text-muted)'}}>No audit entries found.</div>
      ) : (
        <>
          {isMobile ? (
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
              {entries.map((e, i) => {
                const meta = ACTION_LABELS[e.action] || { label: e.action, color: 'tag-muted' };
                return (
                  <div key={i} className="card" style={{padding:'12px 14px'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:6}}>
                      <span className={`tag ${meta.color}`}>{meta.label}</span>
                      <span style={{fontFamily:'var(--font-mono)',fontSize:11,color:'var(--text-muted)',whiteSpace:'nowrap'}}>{formatDate(e.ts)}</span>
                    </div>
                    <div style={{fontFamily:'var(--font-mono)',fontSize:12,color:'var(--text)',marginBottom:4}}>{e.user}</div>
                    {e.detail && <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,overflowWrap:'break-word'}}>{e.detail}</div>}
                    {e.ip && <div style={{fontFamily:'var(--font-mono)',fontSize:11,color:'var(--text-dim)'}}>{e.ip}</div>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="card" style={{padding:0,overflow:'hidden',marginBottom:16}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{background:'var(--surface2)'}}>
                    {['Time','Action','User','Detail','IP'].map(h => (
                      <th key={h} style={{padding:'10px 16px',textAlign:'left',fontSize:11,fontWeight:600,
                        letterSpacing:'0.07em',textTransform:'uppercase',color:'var(--text-muted)',
                        borderBottom:'1px solid var(--border)'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => {
                    const meta = ACTION_LABELS[e.action] || { label: e.action, color: 'tag-muted' };
                    return (
                      <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'10px 16px',fontFamily:'var(--font-mono)',fontSize:11,color:'var(--text-muted)',whiteSpace:'nowrap'}}>{formatDate(e.ts)}</td>
                        <td style={{padding:'10px 16px'}}><span className={`tag ${meta.color}`}>{meta.label}</span></td>
                        <td style={{padding:'10px 16px',fontFamily:'var(--font-mono)',fontSize:12,color:'var(--text)'}}>{e.user}</td>
                        <td style={{padding:'10px 16px',fontSize:12,color:'var(--text-muted)'}}>{e.detail}</td>
                        <td style={{padding:'10px 16px',fontFamily:'var(--font-mono)',fontSize:11,color:'var(--text-dim)'}}>{e.ip}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,flexWrap:'wrap'}}>
              <button className="btn btn-secondary btn-sm"
                disabled={page === 1} onClick={() => setPage(1)}>«</button>
              <button className="btn btn-secondary btn-sm"
                disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
              <span style={{fontSize:13,color:'var(--text-muted)',padding:'0 8px'}}>
                Page {page} of {pages}
              </span>
              <button className="btn btn-secondary btn-sm"
                disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
              <button className="btn btn-secondary btn-sm"
                disabled={page === pages} onClick={() => setPage(pages)}>»</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
